import React from 'react';

const AIAssessmentDisplay = ({ assessment, segment }) => {
  // CORRECT data extraction based on your assessment structure
  const userNBest = assessment?.pronunciation?.NBest?.[0];
  const narratorNBest = assessment?.narrator_pronunciation?.NBest?.[0];
  
  // Check if we have any pronunciation assessment data
  const hasUserAssessment = userNBest && userNBest.PronunciationAssessment;
  const hasNarratorAssessment = narratorNBest && narratorNBest.PronunciationAssessment;

  console.log('AIAssessmentDisplay data:', {
    userNBest,
    narratorNBest,
    hasUserAssessment,
    hasNarratorAssessment
  });

  // If no assessment data at all, show message
  if (!hasUserAssessment && !hasNarratorAssessment) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No pronunciation assessment data available.</p>
        <p className="text-sm mt-2">Complete a recording to see your pronunciation assessment.</p>
      </div>
    );
  }

  // Check if sentence has many words for compact layout
  const hasManyWords = userNBest?.Words?.length >= 15;
  
  return (
    <div className="flex flex-col gap-6 w-full">
      {/* --- USER ASSESSMENT --- */}
      {hasUserAssessment && (
        <div className="flex flex-col gap-4 w-full bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">🎤</span>
            <p className="text-lg font-semibold text-gray-800 text-center">
              Your AI Speech Assessment
            </p>
          </div>

          <div className={`flex ${hasManyWords ? 'flex-col' : 'flex-col lg:flex-row'} gap-4 w-full`}>
            
            {hasManyWords ? (
              /* COMPACT LAYOUT for 15+ words */
              <div className="flex flex-col gap-4">
                {/* Overall Score Card - Centered at top with gap */}
                <div className="w-full max-w-md mx-auto flex flex-col gap-2 p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                  <h4 className="font-semibold text-gray-800 text-center border-b border-gray-200 pb-2 text-sm">
                    Overall Assessment
                  </h4>
                  {Object.entries(userNBest.PronunciationAssessment).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-1 px-2 rounded hover:bg-gray-50 transition-colors">
                      <span className="font-medium text-gray-700 text-xs capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className={`font-semibold text-xs ${
                        typeof value === 'number' 
                          ? value >= 80 ? 'text-green-600' : value >= 40 ? 'text-yellow-600' : 'text-red-600'
                          : 'text-blue-600'
                      }`}>
                        {typeof value === 'number' ? value : value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Words Assessment - Below the score card */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {userNBest.Words.map((word, i) =>
                    word.PronunciationAssessment?.ErrorType === 'Omission' ? (
                      <div key={i} className="w-2 h-2 bg-gray-400 rounded-full opacity-70"></div>
                    ) : (
                      <div
                        key={i}
                        className={`flex flex-col border rounded-lg p-2 min-w-[60px] transition-all hover:scale-105 ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-50 border-green-200'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <p className="text-center font-semibold break-words mb-1 text-sm">
                          {word.Word}
                        </p>

                        {word.Phonemes && (
                          <div className="flex flex-wrap justify-center gap-1 mb-1">
                            {word.Phonemes.map((ph, j) => (
                              <div
                                key={j}
                                className={`h-2 w-2 border rounded-full ${
                                  ph.PronunciationAssessment?.AccuracyScore >= 80
                                    ? 'bg-green-500 border-green-600'
                                    : ph.PronunciationAssessment?.AccuracyScore >= 40
                                    ? 'bg-yellow-500 border-yellow-600'
                                    : 'bg-red-500 border-red-600'
                                }`}
                                title={`${ph.Phoneme}: ${ph.PronunciationAssessment?.AccuracyScore}`}
                              ></div>
                            ))}
                          </div>
                        )}

                        <div className={`text-xs text-center font-bold px-1 py-0.5 rounded ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-500 text-white'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-500 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                          {word.PronunciationAssessment?.AccuracyScore || 'N/A'}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              /* ORIGINAL LAYOUT for <15 words */
              <div className="flex-1 flex flex-col lg:flex-row gap-4">
                {/* Words Assessment */}
                <div className="flex-1 flex flex-wrap gap-3 justify-center">
                  {userNBest.Words.map((word, i) =>
                    word.PronunciationAssessment?.ErrorType === 'Omission' ? (
                      <div key={i} className="w-2 h-2 bg-gray-400 rounded-full opacity-70"></div>
                    ) : (
                      <div
                        key={i}
                        className={`flex flex-col border rounded-xl p-3 min-w-[80px] transition-all hover:scale-105 ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-50 border-green-200'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <p className="text-base text-center font-semibold break-words mb-2">
                          {word.Word}
                        </p>

                        {word.Phonemes && (
                          <div className="flex flex-wrap justify-center gap-1 mb-2">
                            {word.Phonemes.map((ph, j) => (
                              <div
                                key={j}
                                className={`h-3 w-3 border rounded-full ${
                                  ph.PronunciationAssessment?.AccuracyScore >= 80
                                    ? 'bg-green-500 border-green-600'
                                    : ph.PronunciationAssessment?.AccuracyScore >= 40
                                    ? 'bg-yellow-500 border-yellow-600'
                                    : 'bg-red-500 border-red-600'
                                }`}
                                title={`${ph.Phoneme}: ${ph.PronunciationAssessment?.AccuracyScore}`}
                              ></div>
                            ))}
                          </div>
                        )}

                        <div className={`text-sm text-center font-bold px-2 py-1 rounded ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-500 text-white'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-500 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                          {word.PronunciationAssessment?.AccuracyScore || 'N/A'}
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Overall Score Card */}
                <div className="w-full lg:w-72 flex-shrink-0 flex flex-col gap-3 p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                  <h4 className="font-semibold text-gray-800 text-center border-b border-gray-200 pb-2">
                    Overall Assessment
                  </h4>
                  {Object.entries(userNBest.PronunciationAssessment).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-1 px-2 rounded hover:bg-gray-50 transition-colors">
                      <span className="font-medium text-gray-700 text-sm capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className={`font-semibold text-sm ${
                        typeof value === 'number' 
                          ? value >= 80 ? 'text-green-600' : value >= 40 ? 'text-yellow-600' : 'text-red-600'
                          : 'text-blue-600'
                      }`}>
                        {typeof value === 'number' ? value : value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- NARRATOR ASSESSMENT --- */}
      {hasNarratorAssessment && (
        <div className="flex flex-col gap-4 w-full bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">🎧</span>
            <p className="text-lg font-semibold text-gray-800 text-center">
              Narrator Reference Assessment
            </p>
          </div>

          <div className={`flex ${hasManyWords ? 'flex-col' : 'flex-col lg:flex-row'} gap-4 w-full`}>
            
            {hasManyWords ? (
              /* COMPACT LAYOUT for 15+ words */
              <div className="flex flex-col gap-4">
                {/* Narrator Score Card - Centered at top with gap */}
                <div className="w-full max-w-md mx-auto flex flex-col gap-2 p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                  <h4 className="font-semibold text-gray-800 text-center border-b border-gray-200 pb-2 text-sm">
                    Overall Assessment
                  </h4>
                  {Object.entries(narratorNBest.PronunciationAssessment).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-1 px-2 rounded hover:bg-gray-50 transition-colors">
                      <span className="font-medium text-gray-700 text-xs capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className={`font-semibold text-xs ${
                        typeof value === 'number' 
                          ? value >= 80 ? 'text-green-600' : value >= 40 ? 'text-yellow-600' : 'text-red-600'
                          : 'text-blue-600'
                      }`}>
                        {typeof value === 'number' ? value : value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Narrator Words Assessment - Below the score card */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {narratorNBest.Words.map((word, i) =>
                    word.PronunciationAssessment?.ErrorType === 'Omission' ? (
                      <div key={i} className="w-2 h-2 bg-gray-400 rounded-full opacity-70"></div>
                    ) : (
                      <div
                        key={i}
                        className={`flex flex-col border rounded-lg p-2 min-w-[60px] transition-all hover:scale-105 ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-50 border-green-200'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <p className="text-center font-semibold break-words mb-1 text-sm">
                          {word.Word}
                        </p>

                        {word.Phonemes && (
                          <div className="flex flex-wrap justify-center gap-1 mb-1">
                            {word.Phonemes.map((ph, j) => (
                              <div
                                key={j}
                                className={`h-2 w-2 border rounded-full ${
                                  ph.PronunciationAssessment?.AccuracyScore >= 80
                                    ? 'bg-green-500 border-green-600'
                                    : ph.PronunciationAssessment?.AccuracyScore >= 40
                                    ? 'bg-yellow-500 border-yellow-600'
                                    : 'bg-red-500 border-red-600'
                                }`}
                                title={`${ph.Phoneme}: ${ph.PronunciationAssessment?.AccuracyScore}`}
                              ></div>
                            ))}
                          </div>
                        )}

                        <div className={`text-xs text-center font-bold px-1 py-0.5 rounded ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-500 text-white'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-500 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                          {word.PronunciationAssessment?.AccuracyScore || 'N/A'}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              /* ORIGINAL LAYOUT for <15 words */
              <div className="flex-1 flex flex-col lg:flex-row gap-4">
                {/* Narrator Words Assessment */}
                <div className="flex-1 flex flex-wrap gap-3 justify-center">
                  {narratorNBest.Words.map((word, i) =>
                    word.PronunciationAssessment?.ErrorType === 'Omission' ? (
                      <div key={i} className="w-2 h-2 bg-gray-400 rounded-full opacity-70"></div>
                    ) : (
                      <div
                        key={i}
                        className={`flex flex-col border rounded-xl p-3 min-w-[80px] transition-all hover:scale-105 ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-50 border-green-200'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <p className="text-base text-center font-semibold break-words mb-2">
                          {word.Word}
                        </p>

                        {word.Phonemes && (
                          <div className="flex flex-wrap justify-center gap-1 mb-2">
                            {word.Phonemes.map((ph, j) => (
                              <div
                                key={j}
                                className={`h-3 w-3 border rounded-full ${
                                  ph.PronunciationAssessment?.AccuracyScore >= 80
                                    ? 'bg-green-500 border-green-600'
                                    : ph.PronunciationAssessment?.AccuracyScore >= 40
                                    ? 'bg-yellow-500 border-yellow-600'
                                    : 'bg-red-500 border-red-600'
                                }`}
                                title={`${ph.Phoneme}: ${ph.PronunciationAssessment?.AccuracyScore}`}
                              ></div>
                            ))}
                          </div>
                        )}

                        <div className={`text-sm text-center font-bold px-2 py-1 rounded ${
                          word.PronunciationAssessment?.AccuracyScore >= 80
                            ? 'bg-green-500 text-white'
                            : word.PronunciationAssessment?.AccuracyScore >= 40
                            ? 'bg-yellow-500 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                          {word.PronunciationAssessment?.AccuracyScore || 'N/A'}
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Narrator Overall Score Card */}
                <div className="w-full lg:w-72 flex-shrink-0 flex flex-col gap-3 p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                  <h4 className="font-semibold text-gray-800 text-center border-b border-gray-200 pb-2">
                    Overall Assessment
                  </h4>
                  {Object.entries(narratorNBest.PronunciationAssessment).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-1 px-2 rounded hover:bg-gray-50 transition-colors">
                      <span className="font-medium text-gray-700 text-sm capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className={`font-semibold text-sm ${
                        typeof value === 'number' 
                          ? value >= 80 ? 'text-green-600' : value >= 40 ? 'text-yellow-600' : 'text-red-600'
                          : 'text-blue-600'
                      }`}>
                        {typeof value === 'number' ? value : value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssessmentDisplay;